export type RiskRegisterTab = 'triage' | 'executive' | 'sprint' | 'product';

export const DEFAULT_RISK_REGISTER_TAB: RiskRegisterTab = 'triage';

export const TAB_PARAM = 'tab';

interface TabDefinition {
  id: RiskRegisterTab;
  label: string;
  subtitle: string;
}

export const RISK_REGISTER_TABS: TabDefinition[] = [
  {
    id: 'triage',
    label: 'Weekly Triage',
    subtitle: 'What needs attention right now, plus the full register and reference material.',
  },
  {
    id: 'executive',
    label: 'Executive',
    subtitle: 'Trends and totals for the monthly leadership readout.',
  },
  {
    id: 'sprint',
    label: 'Sprint Planning',
    subtitle: 'Direction of risk and where work is sitting, for planning the next sprint.',
  },
  {
    id: 'product',
    label: 'Product report',
    subtitle: 'A plain-language view of how each project is doing.',
  },
];

export interface RiskRegisterSection {
  id: string;
  title: string;
  hint: string;
  tab: RiskRegisterTab;
}

// "Select a view" renders at the top of whichever tab is showing and has no collapse control, so
// it is the one section that carries no tab of its own. It is deliberately kept out of SECTIONS:
// that catalogue drives the per-tab collapsible sections, and each entry there belongs to exactly
// one tab.
export const PICK_SECTION = {
  id: 'pick',
  title: 'Select a view',
  hint: 'Changes apply to every Risk Register section and are saved in this page URL.',
};

// Every section's id, title, hint, and owning tab live here and nowhere else, so a section
// cannot end up on a tab with the wrong title or with no tab at all. The id lists below all
// derive from this one base, so they cannot drift out of sync with each other.
export const SECTIONS: RiskRegisterSection[] = [
  {
    id: 'needs-attention',
    title: 'Needs attention first',
    hint: 'Open items requiring immediate leadership attention.',
    tab: 'triage',
  },
  {
    id: 'queue',
    title: "This week's review queue",
    hint: 'The operational review queue for the next working session.',
    tab: 'triage',
  },
  {
    id: 'recent',
    title: 'Recently added',
    hint: 'Items added to the Risk Register during the selected interval.',
    tab: 'triage',
  },
  {
    id: 'items',
    title: 'All items',
    hint: 'The full filtered register, grouped and sorted using the controls above.',
    tab: 'triage',
  },
  {
    id: 'definitions',
    title: 'How to read this',
    hint: 'Definitions and live data caveats for the Risk Register.',
    tab: 'triage',
  },
  {
    id: 'position',
    title: 'Position',
    hint: 'The current position of the filtered Risk Register view.',
    tab: 'executive',
  },
  {
    id: 'trajectory',
    title: 'Risk trajectory',
    hint: 'How the amount of open risk has moved over time.',
    tab: 'executive',
  },
  {
    id: 'risk-mix',
    title: 'Risk mix over time',
    hint: 'The share of open risk held by each category, month by month.',
    tab: 'executive',
  },
  {
    id: 'area-risk',
    title: 'Open risk by platform area',
    hint: 'Where open risk sits across the platform areas today.',
    tab: 'executive',
  },
  {
    id: 'delivered',
    title: 'What we delivered',
    hint: 'Resolved work and the available change-record evidence.',
    tab: 'executive',
  },
  {
    id: 'decisions',
    title: 'Decisions for the forum',
    hint: 'Measured decision prompts for the leadership forum.',
    tab: 'executive',
  },
  {
    id: 'trend',
    title: 'Direction of risk',
    hint: 'Opened and resolved history for the selected trend window.',
    tab: 'sprint',
  },
  {
    id: 'bottlenecks',
    title: 'Where work sits',
    hint: 'Workflow and ownership bottlenecks with state filters lifted.',
    tab: 'sprint',
  },
  {
    id: 'accountability',
    title: 'Accountability',
    hint: 'Accountability units ranked by the current Risk Register view.',
    tab: 'sprint',
  },
  {
    id: 'monthly',
    title: 'Monthly view by team',
    hint: 'Reconstructed monthly Risk Register state by accountable unit.',
    tab: 'sprint',
  },
  {
    id: 'product-summary',
    title: 'How your projects are doing',
    hint: 'A plain-language read on each project, without the Jira wording.',
    tab: 'product',
  },
  {
    id: 'product-initiatives',
    title: 'By initiative',
    hint: 'Open risk and recent delivery for each initiative.',
    tab: 'product',
  },
  {
    id: 'product-delivery',
    title: 'What shipped',
    hint: 'Work finished recently, month by month.',
    tab: 'product',
  },
];

// Every section except "pick" (static, no toggle) has its own collapse/expand control.
export const COLLAPSIBLE_SECTION_IDS = SECTIONS.map((section) => section.id);

// The full catalogue, used to prove no section is orphaned off every tab.
export const ALL_SECTION_IDS = [PICK_SECTION.id, ...COLLAPSIBLE_SECTION_IDS];

export function isRiskRegisterTab(value: unknown): value is RiskRegisterTab {
  return RISK_REGISTER_TABS.some((tab) => tab.id === value);
}

export function sectionsForTab(tab: RiskRegisterTab): RiskRegisterSection[] {
  return SECTIONS.filter((section) => section.tab === tab);
}

// Mirrors withAsOf in the shared viewState module: an unknown or malformed value falls back to
// the default tab, and the default tab is left out of the URL entirely.
export function tabFromSearch(search: string): RiskRegisterTab {
  const value = new URLSearchParams(search.replace(/^[?#]/, '')).get(TAB_PARAM);
  return isRiskRegisterTab(value) ? value : DEFAULT_RISK_REGISTER_TAB;
}

export function withTab(search: string, tab: RiskRegisterTab): string {
  const params = new URLSearchParams(search.replace(/^[?#]/, ''));
  if (tab === DEFAULT_RISK_REGISTER_TAB) params.delete(TAB_PARAM);
  else params.set(TAB_PARAM, tab);
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
}
